import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup,
    Stack,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link2, PlugZap, Power, RefreshCcw, Shield } from "lucide-react";
import AdminHeader from "../../../adminComponents/header";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";
import SideNavbar from "../../../components/sideNavbar";

const FETCH_MODES = {
    NEW: "new",
    ALL: "all",
};

const MetaLeads = () => {
    const clientId =
        localStorage.getItem("userId") || localStorage.getItem("adminId");

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncingForms, setSyncingForms] = useState(false);
    const [status, setStatus] = useState({
        connected: false,
        fetchMode: FETCH_MODES.NEW,
        selectedForms: [],
    });
    const [scope, setScope] = useState(FETCH_MODES.NEW);
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [selectedForms, setSelectedForms] = useState([]);
    const [actionError, setActionError] = useState("");
    const connectPollRef = useRef(null);

    const ensureClientId = useCallback(() => {
        if (!clientId) {
            setActionError("Missing account context. Please log in again.");
            return false;
        }
        return true;
    }, [clientId]);

    const columns = useMemo(
        () => [
            { field: "id", headerName: "#", width: 70 },
            { field: "leadId", headerName: "Lead ID", flex: 1, minWidth: 180 },
            { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
            { field: "email", headerName: "Email", flex: 1.1, minWidth: 180 },
            { field: "phone", headerName: "Phone", flex: 0.9, minWidth: 150 },
            { field: "pageName", headerName: "Page", flex: 1, minWidth: 150 },
            { field: "formId", headerName: "Form ID", flex: 1, minWidth: 180 },
            {
                field: "createdTime",
                headerName: "Created",
                flex: 1,
                minWidth: 160,
                valueFormatter: (params) =>
                    params?.value
                        ? new Date(params.value).toLocaleString()
                        : "Unknown",
            },
        ],
        []
    );

    const fetchStatus = useCallback(async () => {
        if (!ensureClientId()) return;
        try {
            const res = await axiosInstance.get(
                `${apiEndPointsConfig.metaStatus}/${clientId}`
            );

            setStatus({
                connected: Boolean(res?.data?.connected),
                fetchMode: res?.data?.fetchMode || FETCH_MODES.NEW,
                selectedForms: res?.data?.selectedForms || [],
            });
            setScope(res?.data?.fetchMode || FETCH_MODES.NEW);
        } catch (error) {
            console.error("Failed to load meta status", error);
        }
    }, [clientId, ensureClientId]);

    const fetchLeads = useCallback(
        async (currentScope = scope) => {
            if (!ensureClientId()) return;
            setLoading(true);
            try {
                const res = await axiosInstance.get(apiEndPointsConfig.metaLeads, {
                    params: {
                        clientId,
                        scope: currentScope,
                    },
                });

                const rows =
                    res?.data?.data?.map((lead, index) => ({
                        id: index + 1,
                        leadId: lead.leadId,
                        name: lead.name || "",
                        email: lead.email || "",
                        phone: lead.phone || "",
                        pageName: lead.pageName || "",
                        formId: lead.formId || "",
                        createdTime: lead.createdTime || lead.createdAt,
                    })) || [];

                setLeads(rows);
            } catch (error) {
                console.error("Failed to load meta leads", error);
            } finally {
                setLoading(false);
            }
        },
        [clientId, ensureClientId, scope]
    );

    const loadForms = useCallback(async () => {
        if (!ensureClientId()) return;
        setFormsLoading(true);
        try {
            const res = await axiosInstance.get(
                `${apiEndPointsConfig.metaForms}/${clientId}`
            );
            setForms(res?.data?.data || []);
        } catch (error) {
            console.error("Failed to load forms", error);
        } finally {
            setFormsLoading(false);
        }
    }, [clientId, ensureClientId]);

    const handleSyncForms = async () => {
        if (!ensureClientId()) return;
        setSyncingForms(true);
        try {
            await axiosInstance.post(
                apiEndPointsConfig.metaFormsSync,
                { clientId }
            );
            await loadForms();
        } catch (error) {
            console.error("Failed to sync forms", error);
        } finally {
            setSyncingForms(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        fetchLeads();
    }, [fetchLeads, fetchStatus]);

    useEffect(() => {
        setSelectedForms(status.selectedForms || []);
    }, [status.selectedForms]);

    useEffect(() => {
        if (status.connected) {
            loadForms();
        }
    }, [status.connected, loadForms]);

    useEffect(() => {
        const listener = (event) => {
            if (event?.data?.source === "justtawk-meta" && event?.data?.status) {
                fetchStatus();
                fetchLeads();
            }
        };

        window.addEventListener("message", listener);
        return () => window.removeEventListener("message", listener);
    }, [fetchLeads, fetchStatus]);

    useEffect(() => {
        return () => {
            if (connectPollRef.current) {
                clearInterval(connectPollRef.current);
                connectPollRef.current = null;
            }
        };
    }, []);

    const handleConnect = async () => {
        if (!ensureClientId()) return;
        setConnecting(true);
        setActionError("");
        const popup = window.open(
            "about:blank",
            "meta-auth",
            "width=600,height=700"
        );

        try {
            const res = await axiosInstance.get(apiEndPointsConfig.metaAuthUrl, {
                params: { clientId },
            });
            const url = res?.data?.url;
            if (!url) {
                if (popup && !popup.closed) popup.close();
                setActionError("Unable to start Facebook auth. Please try again.");
                return;
            }

            // Fallback to same-tab auth when popup is blocked by browser settings.
            if (popup && !popup.closed) {
                popup.location.href = url;
            } else {
                window.location.assign(url);
                return;
            }

            const startTime = Date.now();
            if (connectPollRef.current) {
                clearInterval(connectPollRef.current);
            }
            connectPollRef.current = setInterval(async () => {
                const timedOut = Date.now() - startTime > 2 * 60 * 1000;
                if (popup.closed || timedOut) {
                    clearInterval(connectPollRef.current);
                    connectPollRef.current = null;
                    await fetchStatus();
                    await fetchLeads();
                }
            }, 1500);
        } catch (error) {
            console.error("Failed to start Facebook connect", error);
            if (popup && !popup.closed) popup.close();
            setActionError("Failed to start Facebook connect. Please try again.");
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!ensureClientId()) return;
        try {
            await axiosInstance.post(
                apiEndPointsConfig.metaDisconnect,
                { clientId }
            );
            setStatus({ connected: false, fetchMode: FETCH_MODES.NEW, selectedForms: [] });
            setSelectedForms([]);
            setLeads([]);
        } catch (error) {
            console.error("Failed to disconnect", error);
        }
    };

    const handleScopeChange = async (value) => {
        setScope(value);
        await fetchLeads(value);
        await saveSettings(value, selectedForms);
    };

    const handleManualSync = async () => {
        if (!ensureClientId()) return;
        setSyncing(true);
        try {
            await axiosInstance.post(
                apiEndPointsConfig.metaManualSync,
                { clientId, scope }
            );
            await fetchLeads();
        } catch (error) {
            console.error("Manual sync failed", error);
        } finally {
            setSyncing(false);
        }
    };

    const saveSettings = async (fetchModeValue = scope, selectedFormsValue = status.selectedForms) => {
        if (!ensureClientId()) return;
        try {
            const payload = {
                clientId,
                fetchMode: fetchModeValue,
                selectedForms: selectedFormsValue,
            };
            await axiosInstance.post(apiEndPointsConfig.metaSettings, payload);
            setStatus((prev) => ({
                ...prev,
                fetchMode: fetchModeValue,
                selectedForms: selectedFormsValue,
            }));
        } catch (error) {
            console.error("Failed to save settings", error);
        }
    };

    const toggleForm = (form) => {
        const exists = selectedForms.some((f) => f.formId === form.formId);
        if (exists) {
            setSelectedForms(selectedForms.filter((f) => f.formId !== form.formId));
        } else {
            setSelectedForms([...selectedForms, form]);
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    ml: { xs: "0px", md: "280px" },
                }}
            >
                <SideNavbar />
                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "initial",
                        },
                    }}
                >
                    <AdminHeader title={"Meta Leads"} />
                </Box>

                <Box sx={{ p: 2 }}>
                    {actionError && (
                        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionError("")}>
                            {actionError}
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Shield size={18} color="#6366F1" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Connection
                                        </Typography>
                                        <Chip
                                            label={status.connected ? "Connected" : "Disconnected"}
                                            color={status.connected ? "success" : "default"}
                                            size="small"
                                            sx={{ ml: "auto" }}
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={1} mt={2}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<Link2 size={16} />}
                                            onClick={handleConnect}
                                            disabled={connecting}
                                        >
                                            {connecting ? "Connecting..." : "Connect with Facebook"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={<Power size={16} />}
                                            onClick={handleDisconnect}
                                            disabled={!status.connected}
                                        >
                                            Disconnect
                                        </Button>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" gutterBottom>
                                        Select Lead Forms
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: "1px solid #eee",
                                            borderRadius: 1,
                                            maxHeight: 240,
                                            overflow: "auto",
                                            p: 1,
                                            bgcolor: "#fafafa",
                                        }}
                                    >
                                        {formsLoading && (
                                            <Stack direction="row" spacing={1} alignItems="center" p={1}>
                                                <CircularProgress size={16} />
                                                <Typography variant="body2">Loading forms...</Typography>
                                            </Stack>
                                        )}
                                        {!formsLoading && forms.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" p={1}>
                                                No forms found. Ensure the Facebook account has pages with lead forms.
                                            </Typography>
                                        )}
                                        <FormGroup>
                                            {forms.map((form) => (
                                                <FormControlLabel
                                                    key={form.formId}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedForms.some((f) => f.formId === form.formId)}
                                                            onChange={() => toggleForm(form)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={`${form.formName || "Untitled"} (${form.pageName || form.pageId})`}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                    <Stack direction="row" spacing={1} mt={1}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleSyncForms}
                                            disabled={!status.connected || syncingForms}
                                        >
                                            {syncingForms ? "Syncing forms..." : "Reload/Sync Forms"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => saveSettings(scope, selectedForms)}
                                            disabled={selectedForms.length === 0}
                                        >
                                            Save Selection
                                        </Button>
                                        <Typography variant="caption" color="text.secondary">
                                            Select one or more forms to enable syncing.
                                        </Typography>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" gutterBottom>
                                        Fetch Mode
                                    </Typography>
                                    <FormControl>
                                        <RadioGroup
                                            value={scope}
                                            onChange={(e) => handleScopeChange(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value={FETCH_MODES.NEW}
                                                control={<Radio size="small" />}
                                                label="Only new leads"
                                            />
                                            <FormControlLabel
                                                value={FETCH_MODES.ALL}
                                                control={<Radio size="small" />}
                                                label="Fetch older leads too"
                                            />
                                        </RadioGroup>
                                    </FormControl>

                                    <Divider sx={{ my: 2 }} />

                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshCcw size={16} />}
                                        onClick={handleManualSync}
                                        disabled={!status.connected || syncing || selectedForms.length === 0}
                                    >
                                        {syncing ? "Syncing..." : "Sync now"}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Card>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={2}
                                    >
                                        <Typography variant="h6" fontWeight={600}>
                                            Meta Leads
                                        </Typography>
                                        <Chip
                                            icon={<PlugZap size={14} />}
                                            label={`${leads.length} lead${leads.length === 1 ? "" : "s"
                                                }`}
                                            size="small"
                                        />
                                    </Stack>

                                    <div style={{ height: 520, width: "100%" }}>
                                        <DataGrid
                                            rows={leads}
                                            columns={columns}
                                            density="comfortable"
                                            disableColumnMenu
                                            loading={loading}
                                            getRowId={(row) => `${row.leadId}-${row.id}`}
                                        />
                                    </div>

                                    {loading && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            mt={1}
                                        >
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">
                                                Loading leads...
                                            </Typography>
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default MetaLeads;
